import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { GridLegacy as Grid, IconButton, SxProps, Typography } from '@mui/material';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useDestroyPreference, usePreferenceDeleteCheck } from '../../services/react-query/preferences';
import { queryClient } from '../../services/react-query/queryClient';
import { queryKeys } from '../../services/react-query/queryKeys';
import { listPageTableRowStyling } from '../../styles/sxStyling';
import { Preference, PREFERENCES_VALID_KEYS } from '../../types/Preference';
import { Loading } from '../../uikit';
import { eventTimelineDateNameGrid } from '../common/eventTimeline/eventTimelineStyles';
import ConfirmationModal from '../ConfirmationModal';
import PreferenceDeleteCheckModal from './PreferenceDeleteCheckModal';
import PreferenceFormModal from './PreferenceFormModal'; // ← path per where you saved it
import { getPreferenceRowStyles } from '../../utility/PreferenceColorRow';

interface PreferenceItemProps {
  preference: Preference;
  preferenceKey: PREFERENCES_VALID_KEYS;
  setSnackbarMessage: Dispatch<SetStateAction<string | null>>;
}

export default function PreferenceItem({ preference, setSnackbarMessage, preferenceKey }: PreferenceItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [deleteTargetUuid, setDeleteTargetUuid] = useState<string | null>(null);
  const [deleteUsages, setDeleteUsages] = useState<any[]>([]);
  const [showDeleteCheckModal, setShowDeleteCheckModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { refetch: refetchDeleteCheck, isFetching: isDeleteCheckFetching } = usePreferenceDeleteCheck({
    uuid: deleteTargetUuid || '',
    enabled: false,
  });

  const { mutate: destroyPreferenceMutate } = useDestroyPreference({
    uuid: deleteTargetUuid || '',
    onSuccessCallback: () => {
      resetDeleteState();
      setSnackbarMessage('Preference deleted successfully!');
      queryClient.invalidateQueries({
        queryKey: [queryKeys.allPreferences, { filters: { keys: [preference.key] } }],
      });
    },
  });

  const resetDeleteState = () => {
    setDeleteTargetUuid(null);
    setDeleteUsages([]);
    setShowDeleteCheckModal(false);
    setShowDeleteModal(false);
  };

  useEffect(() => {
    if (!deleteTargetUuid) return;
    (async () => {
      try {
        const result = await refetchDeleteCheck();
        if (result.data && result.data?.length > 0) {
          setDeleteUsages(result.data);
          setShowDeleteCheckModal(true);
        } else {
          setShowDeleteModal(true);
        }
      } catch (error) {
        console.error('Error fetching preference usages:', error);
        setSnackbarMessage('Error checking preference usages.');
        resetDeleteState();
      }
    })();
  }, [deleteTargetUuid, refetchDeleteCheck, setSnackbarMessage]);

  const { rowSx, textSx, iconSx } = getPreferenceRowStyles(preference.color);

  return (
    <>
      <Grid
        key={preference.uuid}
        container
        sx={
          {
            ...listPageTableRowStyling,
            ...eventTimelineDateNameGrid,
            ...rowSx,
          } as SxProps
        }
      >
        <Grid item xs={4}>
          <Typography sx={textSx}>{preference.value}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography sx={textSx}>{preference.description}</Typography>
        </Grid>
        <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <IconButton onClick={() => setIsEditing(true)} size="small" sx={iconSx}>
            <EditIcon />
          </IconButton>

          {isDeleteCheckFetching ? (
            <Loading size={22} />
          ) : (
            <IconButton onClick={() => setDeleteTargetUuid(preference.uuid)} size="small" sx={iconSx}>
              <DeleteIcon />
            </IconButton>
          )}
        </Grid>
      </Grid>

      {isEditing && (
        <PreferenceFormModal
          uuid={preference.uuid}
          preferenceKey={preferenceKey}
          isOpen={isEditing}
          onCancel={() => {
            setIsEditing(false);
            resetDeleteState();
          }}
          setPreferenceSnackbarMessage={setSnackbarMessage}
        />
      )}

      {showDeleteCheckModal && (
        <PreferenceDeleteCheckModal isOpen={showDeleteCheckModal} usages={deleteUsages} onClose={() => setShowDeleteCheckModal(false)} />
      )}

      {showDeleteModal && (
        <ConfirmationModal
          text="Are you sure you want to delete this preference? This operation is irreversible!"
          isOpen={showDeleteModal}
          onCancel={resetDeleteState}
          onConfirm={() => destroyPreferenceMutate()}
        />
      )}
    </>
  );
}
